
import { networkService } from "@/services/network.service";
import { syncService } from "@/services/sync.service";
import { categoryDataService } from "./categoryDataService";
import { categoryOperationsService } from "./categoryOperations.service";
import { UserCategory } from "@/types";
import OfflineLogger from "@/utils/offlineLogger";

export class CategorySyncService {
  async loadLocalCategories(): Promise<UserCategory[]> {
    return categoryDataService.loadLocalCategories();
  }

  async syncFromServer(): Promise<UserCategory[]> {
    if (!networkService.isOnline()) {
      OfflineLogger.info('CategorySyncService.syncFromServer', 'Offline mode, loading local categories');
      return this.loadLocalCategories();
    }

    try {
      OfflineLogger.info('CategorySyncService.syncFromServer', 'Starting server sync');

      const serverCategories = await categoryDataService.fetchServerCategories();

      if (serverCategories.length === 0) {
        const localCategories = await this.loadLocalCategories();
                
        return localCategories;
      }

      // Convert to the format expected by updateLocalCategories
      const serverCategoriesForLocal = serverCategories.map(cat => ({
        id: cat.id,
        user_id: cat.userId,
        name: cat.name,
        icon: cat.icon,
        color: cat.color,
        text_color: cat.textColor,
        position: cat.position,
        visibile: cat.visibile,
        created_at: cat.createdAt.toISOString(),
        updated_at: cat.updatedAt.toISOString()
      }));

      await categoryDataService.updateLocalCategories(serverCategoriesForLocal);
      
      OfflineLogger.info('CategorySyncService.syncFromServer', `Synced ${serverCategories.length} categories from server`);
      
      return serverCategories;
    } catch (error) {
      OfflineLogger.error('CategorySyncService.syncFromServer', error);
      return this.loadLocalCategories();
    }
  }

  async syncPendingOperations(): Promise<void> {
    if (!networkService.isOnline()) {
      OfflineLogger.info('CategorySyncService.syncPendingOperations', 'Offline mode, skipping pending operations sync');
      return;
    }

    try {
      const userId = await categoryDataService.getCurrentUserId();
      if (!userId) return;

      await syncService.syncEntity('categories', userId);
      OfflineLogger.info('CategorySyncService.syncPendingOperations', 'Pending category operations synced');
    } catch (error) {
      OfflineLogger.error('CategorySyncService.syncPendingOperations', error);
    }
  }

  async getPendingOperationsCount(): Promise<number> {
    return categoryOperationsService.getPendingOperationsCount();
  }

  async performFullSync(): Promise<UserCategory[]> {
    try {
      OfflineLogger.info('CategorySyncService.performFullSync', 'Starting full category sync');
      
      await this.syncPendingOperations();
      const categories = await this.syncFromServer();
      
      OfflineLogger.info('CategorySyncService.performFullSync', 'Full category sync completed');
      return categories;
    } catch (error) {
      OfflineLogger.error('CategorySyncService.performFullSync', error);
      return this.loadLocalCategories();
    }
  }
}

export const categorySyncService = new CategorySyncService();
